import React, { useState, useEffect } from 'react';
import { Form, Button, Collapse, Typography, message, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLifestyleCategoriesI18n } from '../utils/lifestyleCategoriesI18n';
import LifestyleCategoryFormI18n from './intake/LifestyleCategoryFormI18n';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const PublicSurveyLifestyleQuestionnaires = ({ patient, initialData, onSubmit, onCancel, onFormRef, loading }) => {
  const [form] = Form.useForm();
  const [activeKey, setActiveKey] = useState(['nutrition']);
  const { t } = useTranslation();

  // Get i18n-enabled lifestyle categories
  const lifestyleCategories = useLifestyleCategoriesI18n();

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      form.setFieldsValue(initialData);
    }
  }, [initialData, form]);

  // Pass form reference to parent component for data capture
  useEffect(() => {
    if (onFormRef) {
      onFormRef(form);
    }
  }, [form, onFormRef]);

  const handleSubmit = async () => {
    try {
      // Pass the form instance to parent for validation
      await onSubmit(null, form);
    } catch (error) {
      console.error('Error in lifestyle submission:', error);
      message.error(t('healthSurvey.surveyFailed'));
    }
  };

  const transformAndSubmitData = async () => {
    try {
      const values = form.getFieldsValue();

      // Transform data to standardized parameter names (same as intake workflow)
      const standardizedData = {};
      
      // Process each lifestyle category
      lifestyleCategories.forEach(category => {
        const categoryData = values[category.key] || {};
        
        // Transform each parameter in the category
        category.parameters.forEach(param => {
          if (param.type !== 'Header' && categoryData[param.key] !== undefined) {
            const standardizedKey = `lifestyle_${category.key}_${param.key}`;
            let value = categoryData[param.key];
            
            // Handle BooleanCheckbox type - convert array to object
            if (param.type === 'BooleanCheckbox' && Array.isArray(value)) {
              const booleanObj = {};
              if (param.options) {
                param.options.forEach(option => {
                  booleanObj[option.value] = value.includes(option.value);
                });
              }
              value = booleanObj;
            }
            
            standardizedData[standardizedKey] = value;
          }
        });
      });

      console.log('Public Survey Lifestyle Data (Standardized):', standardizedData);
      
      onSubmit(standardizedData);
    } catch (error) {
      console.error('Public survey lifestyle submission failed:', error);
      message.error('Failed to submit lifestyle questionnaires. Please try again.');
    }
  };

  return (
    <div style={{ padding: '16px 0' }}>
      <Title level={4} style={{ marginBottom: 16 }}>
        {t('lifestyle.headers.lifestyleQuestionnaires')}
      </Title>
      
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        {t('lifestyle.headers.lifestyleDescription')}
      </Text>
      
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        size="large"
      >
        <Collapse 
          activeKey={activeKey} 
          onChange={setActiveKey}
          className="lifestyle-questionnaires-collapse"
        >
          {lifestyleCategories.map(category => (
            <Panel 
              header={category.title} 
              key={category.key}
              className="lifestyle-category-panel"
            >
              <LifestyleCategoryFormI18n
                category={category}
                formName={category.key}
              />
            </Panel>
          ))}
        </Collapse>
      </Form>

      {/* Custom footer for this step */}
      <div style={{ 
        marginTop: 24, 
        textAlign: 'right',
        borderTop: '1px solid #f0f0f0',
        paddingTop: 16
      }}>
        <Space>
          <Button onClick={onCancel} size="large" disabled={loading}>
            {t('healthSurvey.previous')}
          </Button>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            loading={loading}
            size="large"
          >
            {t('healthSurvey.submitSurvey')}
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default PublicSurveyLifestyleQuestionnaires;
